/**
 * E002-T040: workflow engine API Client
 *
 * sl-n8i1.3: Refactored to use workspace-scoped routes
 *
 * Client for communicating with workflow engine via Backend Proxy.
 * All workflow engine API calls go through the backend to keep credentials secure.
 *
 * Backend Endpoints (sl-n8i1.2):
 * - GET /api/workspace//workflow/status - Check if automation is configured
 * - GET /api/workspace//workflow/workflows - List all workflows
 * - GET /api/workspace//workflow/workflows/:id/executions/latest - Get latest execution
 */

import {
  type AutomationSafetyDiagnosticPage,
  AutomationSafetyDiagnosticPageSchema,
} from '@streamlain/automation-safety-contract';
import {
  isWorkflowPublishRefusal,
  type WorkflowPublishIssue,
} from '@streamlain/workflow-field-schemas';
import { LiveData, Service } from '@toeverything/infra';

import {
  markBackendAvailable,
  markBackendUnavailable,
  probeBackendAvailabilityNow,
} from '../../../utils/backend-availability';
import { defaultWorkspaceApiClient, WorkspaceApiError } from '../../cloud';
import type { WorkspaceService } from '../../workspace';
import { readWorkflowDocDefinition } from '../workflow-viewer/doc/workflow-doc-registration';
import type {
  ValidationReport,
  WorkflowVersion,
} from '../workflow-viewer/graph-types';
import type {
  Category,
  ClassificationRuleDTO,
  ClassificationSuggestion,
  CompatibilityEvaluation,
  CompatibilityIssue,
  CopilotMigrationResult,
  DiscoveryResult,
  ImportOptions,
  ImportResult,
  PaginatedResponse,
  WorkflowContext,
  WorkflowEngineNode,
  WorkflowTemplate,
} from './marketplace-types';
import { WorkflowPublishRefusedError } from './workflow-publish-refused-error';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowOwner {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface EngineWorkflow {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  categories?: Array<{ id: string; name: string }>;
  nodes?: EngineNode[];
  owner?: WorkflowOwner;
}

export interface EngineNode {
  id: string;
  name: string;
  type: string;
  parameters?: Record<string, unknown>;
}

export interface WorkflowWithWebhook extends EngineWorkflow {
  hasWebhook: boolean;
  webhookPath?: string;
  webhookMethod?: string;
  webhookUrl?: string;
}

export interface WorkflowHeader {
  id: string;
  name: string;
  active: boolean;
  categories: Array<{ id: string; name: string }>;
}

/** SWR execution metadata from the backend endpoint. */
export interface EngineExecution {
  id: string;
  mode: string;
  startedAt: string;
  completedAt?: string | null;
  error?: string | null;
  status:
    | 'success'
    | 'error'
    | 'running'
    | 'waiting'
    | 'completed'
    | 'failed'
    | 'cancelled';
}

/** SWR-native step result from the execution endpoint. */
export interface EngineStepResult {
  nodeId: string | null;
  nodeName: string;
  nodeType: string;
  status: 'success' | 'error';
  durationMs: number;
  error?: string;
}

/** Execution detail with SWR-native step results. */
export interface EngineExecutionDetail extends EngineExecution {
  durationMs?: number | null;
  steps?: EngineStepResult[];
}

export type { AutomationSafetyDiagnosticPage };

// =============================================================================
// E007-T025: Workflow Schema Types
// =============================================================================

export interface WorkflowSchemaField {
  id: string;
  field: string;
  type:
    | 'attachment'
    | 'text'
    | 'rich-text'
    | 'number'
    | 'checkbox'
    | 'select'
    | 'multi-select'
    | 'date'
    | 'json'
    | 'row-list';
  description: string;
  default?: unknown;
}

export interface WorkflowSchemaOutputField {
  id: string;
  name: string;
  type: string;
  description: string;
  required: boolean;
}

export interface WorkflowSchema {
  name: string;
  version: string;
  description?: string;
  input: {
    required: WorkflowSchemaField[];
    optional: WorkflowSchemaField[];
  };
  output: Record<string, WorkflowSchemaOutputField>;
}

export interface WorkflowPreflightSchemaField {
  field: string;
  type: string;
  description: string;
  source?: string;
}

/** A finding that keeps the workflow from being published. */
export interface WorkflowPublishPreflightError {
  code: string;
  /** The server's English sentence, for logs. */
  message: string;
  referenceId?: string;
  issues: readonly WorkflowPublishIssue[];
}

export interface WorkflowPublishPreflight {
  validationReport: {
    valid: boolean;
    errors: WorkflowPublishPreflightError[];
    warnings: ValidationReport['warnings'];
  };
  expectedInput: WorkflowPreflightSchemaField[];
  expectedOutput: WorkflowPreflightSchemaField[];
  currentInput: WorkflowPreflightSchemaField[];
  currentOutput: WorkflowPreflightSchemaField[];
  sourceContracts: string[];
  clean: boolean;
}

export interface WorkflowDeleteUsage {
  configId: string;
  blockId: string;
  docId: string;
  /** Automation block title (human-readable) */
  automationName: string;
  /** @deprecated Use automationName instead */
  workflowName?: string;
}

export interface WorkflowDeleteImpact {
  workflowId: string;
  workflowName: string;
  usages: WorkflowDeleteUsage[];
}

export interface WorkflowAutomationUsage {
  configId: string;
  blockId: string;
  docId: string | null;
  automationName: string;
  canOpenDoc: boolean;
}

export interface WorkflowUsageImpact {
  workflowId: string;
  workflowName: string;
  usages: WorkflowAutomationUsage[];
}

// Backend response types
interface WorkflowsResponse {
  workflows: WorkflowWithWebhook[];
}

// =============================================================================
// Service
// =============================================================================

const WORKFLOW_REQUEST_TIMEOUT_MS = 5000;

/** The server answered and refused: asking again gives the same answer. */
export class WorkflowServerRefusal extends Error {
  override readonly name = 'WorkflowServerRefusal';
}

/** A client error the server answered about the request itself. */
const refusesStep = (status: number) =>
  status >= 400 &&
  status < 500 &&
  status !== 401 &&
  status !== 408 &&
  status !== 429;

export class WorkflowClientService extends Service {
  /**
   * sl-n8i1: Workspace service for workspace-scoped workflow engine credentials
   */
  constructor(private readonly workspaceService: WorkspaceService) {
    super();
  }

  /**
   * sl-n8i1.3: Get current workspace ID
   */
  private get workspaceId(): string {
    return this.workspaceService.workspace.id;
  }

  /**
   * DRY URL builder for workflow API endpoints
   * Routes: /api/workspace/:workspaceId/workflow/*
   */
  private buildUrl(path: string): string {
    return `/api/workspace/${encodeURIComponent(this.workspaceId)}/workflow${path}`;
  }

  private request(
    input: string | URL,
    init: RequestInit = {}
  ): Promise<Response> {
    const path = input.toString();
    const method = init.method ?? 'GET';
    const isMutation = method !== 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId: this.workspaceId,
      path,
      init,
      category: 'workspace-api',
      key: `${method}:${path}`,
      mode: isMutation ? 'foreground' : 'background',
      priority: isMutation ? 'foreground' : 'visible',
    });
  }

  /**
   * sl-za0v: SWR is always configured — no provisioning needed.
   * Kept as LiveData for backwards compatibility with WorkflowService.isEngineConfigured$.
   */
  readonly isConfigured$ = new LiveData<boolean>(true);

  /**
   * sl-za0v: Always true — SWR needs no async status check.
   */
  readonly statusChecked$ = new LiveData<boolean>(true);

  /**
   * Error state
   */
  readonly error$ = new LiveData<string | null>(null);

  /**
   * Loading state
   */
  readonly isLoading$ = new LiveData<boolean>(false);

  /**
   * Cached workflows
   */
  readonly workflows$ = new LiveData<WorkflowWithWebhook[]>([]);

  /**
   * sl-za0v: SWR is always configured — no provisioning needed.
   */
  get isConfigured(): boolean {
    return true;
  }

  /**
   * Reset state on workspace change.
   */
  resetForWorkspaceChange(): void {
    this.workflows$.next([]);
    this.error$.next(null);
    this.isLoading$.next(false);
  }

  /**
   * sl-za0v: No-op — SWR needs no status check (always configured).
   * Kept for call-site compatibility (returns true immediately).
   */
  async checkStatus(): Promise<boolean> {
    return true;
  }

  /**
   * sl-1ahs.3.1: Lightweight health check via the central backend
   * availability manager.
   * Returns true if backend is reachable, false otherwise.
   * Used by WorkflowService.connectivity$ for polling.
   */
  async checkHealth(): Promise<boolean> {
    return probeBackendAvailabilityNow();
  }

  /**
   * Create a new empty workflow in workflow engine.
   */
  async createWorkflow(
    name: string,
    template?: string
  ): Promise<WorkflowWithWebhook> {
    const response = await this.request(this.buildUrl('/workflows'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, template }),
    });

    const data: { workflow: WorkflowWithWebhook } = await response.json();

    // Add to cache
    this.workflows$.next([...this.workflows$.value, data.workflow]);

    return data.workflow;
  }

  /**
   * Fetch all workflows from backend proxy
   * sl-n8i1.3: Uses workspace-scoped route
   */
  async fetchWorkflows(): Promise<WorkflowWithWebhook[]> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const response = await this.request(this.buildUrl('/workflows'), {
        credentials: 'include',
        signal: AbortSignal.timeout(WORKFLOW_REQUEST_TIMEOUT_MS),
      });

      const data: WorkflowsResponse = await response.json();

      markBackendAvailable();
      this.workflows$.next(data.workflows);
      return data.workflows;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      markBackendUnavailable(error);
      this.error$.next(`network_error: ${message}`);
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Get recent executions for a workflow
   * Note: Not yet implemented in backend proxy
   */
  async getExecutions(
    _workflowId: string,
    _limit = 5
  ): Promise<EngineExecution[]> {
    console.warn('[WorkflowClient] getExecutions not yet implemented');
    return [];
  }

  /**
   * Get latest execution for a workflow with step details
   * sl-n8i1.3: Uses workspace-scoped route
   */
  async getLatestExecution(
    workflowId: string
  ): Promise<EngineExecutionDetail | null> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/executions/latest`),
      { credentials: 'include' }
    );

    // 204 No Content = no executions yet (expected, not an error)
    if (response.status === 204) {
      return null;
    }

    const data: { execution: EngineExecutionDetail | null } =
      await response.json();
    return data.execution;
  }

  async getAutomationSafety(
    workflowId: string,
    configId: string
  ): Promise<AutomationSafetyDiagnosticPage> {
    const response = await this.request(
      this.buildUrl(
        `/workflows/${encodeURIComponent(workflowId)}/automations/${encodeURIComponent(configId)}/safety`
      ),
      { credentials: 'include' }
    );
    return AutomationSafetyDiagnosticPageSchema.parse(await response.json());
  }

  /**
   * sl-dpq2: Update workflow (rename, activate/deactivate)
   * Proxied through backend to workflow engine REST API.
   */
  async updateWorkflow(
    id: string,
    updates: {
      name?: string;
      active?: boolean;
      categories?: Array<{ id: string; name: string }>;
    }
  ): Promise<void> {
    await this.request(this.buildUrl(`/workflows/${id}`), {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    // Invalidate cache
    const cached = this.workflows$.value;
    const idx = cached.findIndex((wf: WorkflowWithWebhook) => wf.id === id);
    if (idx >= 0) {
      const updated = { ...cached[idx], ...updates };
      const newList = [...cached];
      newList[idx] = updated;
      this.workflows$.next(newList);
    }
  }

  /**
   * The step the editor inserts into the workflow document: refused by the
   * lifecycle gate, or seeded for its type with the slots of its node type.
   * Null for a branch or a loop, which take their shape from the graph.
   */
  async prepareStep(
    workflowId: string,
    body: {
      stepType: string;
      stepName?: string;
      stepConfig?: Record<string, unknown>;
      branchType?: 'branch_xor' | 'branch_or' | 'branch_and';
    }
  ): Promise<Record<string, unknown> | null> {
    let response: Response;
    try {
      response = await this.request(
        this.buildUrl(
          `/workflows/${encodeURIComponent(workflowId)}/prepare-step`
        ),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
    } catch (error) {
      // The request rejects on every non-2xx answer. An answer about the step
      // refuses it; without one (no network, the server busy or down, the
      // session to renew) the step waits.
      if (error instanceof WorkspaceApiError && refusesStep(error.status))
        throw new WorkflowServerRefusal(error.message);
      throw error;
    }
    const { preparedStep } = (await response.json()) as {
      preparedStep: Record<string, unknown> | null;
    };
    return preparedStep;
  }

  /**
   * List all workflow categories for current workspace.
   */
  async getCategories(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await this.request(this.buildUrl('/categories'), {
        credentials: 'include',
        signal: AbortSignal.timeout(WORKFLOW_REQUEST_TIMEOUT_MS),
      });
      const data: { categories: Array<{ id: string; name: string }> } =
        await response.json();
      markBackendAvailable();
      return data.categories ?? [];
    } catch (err) {
      // Only an unreachable server marks the backend offline; an answer such
      // as 403 is the error of this request alone.
      markBackendUnavailable(err);
      throw err;
    }
  }

  /**
   * Create a new workflow category.
   */
  async createCategory(name: string): Promise<{ id: string; name: string }> {
    const response = await this.request(this.buildUrl('/categories'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const data: { category: { id: string; name: string } } =
      await response.json();
    return data.category;
  }

  /**
   * Delete a workflow category.
   */
  async deleteCategory(categoryId: string): Promise<void> {
    await this.request(this.buildUrl(`/categories/${categoryId}`), {
      method: 'DELETE',
      credentials: 'include',
    });
  }

  /**
   * Get workflow delete impact (automations currently linked to this workflow).
   */
  async fetchWorkflowDeleteImpact(
    workflowId: string
  ): Promise<WorkflowDeleteImpact> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/delete-impact`),
      { method: 'GET', credentials: 'include' }
    );

    return (await response.json()) as WorkflowDeleteImpact;
  }

  /**
   * Get user-visible automation usages for a workflow.
   * Doc ids/titles are only returned when the current user can open that doc.
   */
  async fetchWorkflowUsages(workflowId: string): Promise<WorkflowUsageImpact> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/usages`),
      { method: 'GET', credentials: 'include' }
    );

    return (await response.json()) as WorkflowUsageImpact;
  }

  /**
   * Delete a workflow via workflow engine REST API.
   */
  async deleteWorkflow(
    id: string,
    options?: { force?: boolean }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.force) params.set('force', 'true');
    const suffix = params.toString();
    await this.request(
      this.buildUrl(`/workflows/${id}${suffix ? `?${suffix}` : ''}`),
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    // Remove from cache
    this.workflows$.next(
      this.workflows$.value.filter((wf: WorkflowWithWebhook) => wf.id !== id)
    );

    // Notify automation blocks that this workflow no longer exists
    console.log('[WorkflowClient] Dispatching workflow:deleted for', id);
    window.dispatchEvent(
      new CustomEvent('workflow:deleted', { detail: { workflowId: id } })
    );
  }

  /**
   * Fetch workflow schema from the SWR backend endpoint.
   * Returns the input/output schema defined in the workflow definition.
   */
  async fetchWorkflowSchema(
    workflowId: string
  ): Promise<WorkflowSchema | null> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/schema`),
      { method: 'GET', credentials: 'include' }
    );

    const data = await response.json();
    if (data.schema) {
      return data.schema as WorkflowSchema;
    }
    return null;
  }

  // ===========================================================================
  // sl-dpq2.3: History and publishing
  // ===========================================================================

  /**
   * Takes the state the document of the workflow had at a history entry over
   * as draft. The server rebuilds that state from the stored history.
   */
  async restoreWorkflowHistory(
    workflowId: string,
    timestamp: string
  ): Promise<void> {
    await this.request(
      this.buildUrl(`/workflows/${workflowId}/restore-history`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timestamp }),
      }
    );
  }

  /**
   * Publish (activate) a workflow.
   */
  async publishWorkflow(
    workflowId: string,
    userId?: string
  ): Promise<WorkflowVersion> {
    let response: Response;
    try {
      response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/publish`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
    } catch (error) {
      // The request rejects on every non-2xx answer; a refusal that names its
      // fields travels on as its own error so the editor can word it.
      if (
        error instanceof WorkspaceApiError &&
        isWorkflowPublishRefusal(error.responseBody)
      ) {
        throw new WorkflowPublishRefusedError(
          error.message,
          error.responseBody.issues
        );
      }
      throw error;
    }
    const data: { snapshot: WorkflowVersion; warning?: string } =
      await response.json();
    if (data.warning) {
      // Attach warning so caller can display it
      (
        data.snapshot as WorkflowVersion & { _publishWarning?: string }
      )._publishWarning = data.warning;
    }
    return data.snapshot;
  }

  /**
   * Unpublish (deactivate) a workflow.
   */
  async unpublishWorkflow(
    workflowId: string,
    userId?: string
  ): Promise<WorkflowVersion> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/unpublish`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }
    );
    const data: { snapshot: WorkflowVersion } = await response.json();
    return data.snapshot;
  }

  /**
   * Discard draft snapshots for a workflow (server-side metadata only).
   */
  async discardWorkflowDraft(workflowId: string): Promise<void> {
    await this.request(
      this.buildUrl(`/workflows/${workflowId}/discard-draft`),
      {
        method: 'POST',
        credentials: 'include',
      }
    );
  }

  // ===========================================================================
  // Workflow header and document reads
  // ===========================================================================

  /**
   * Get the current viewer state for a workflow.
   * Returns the header of the workflow and the fingerprint of its published
   * definition. The content comes from the workflow document.
   */
  async getWorkflowCurrent(workflowId: string): Promise<{
    workflow: WorkflowHeader;
    publishedVersion: number | null;
    publishedDefinitionHash: string | null;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/current`),
        { credentials: 'include' }
      );

      const data = await response.json();
      const wf = data.workflow;

      // Sync cache: backend is authoritative for active/name/categories.
      // Without this, list-derived views (sidebar execution-check) stay stale
      // while the viewer shows correct state → divergent UI.
      const cached = this.workflows$.value;
      const idx = cached.findIndex(c => c.id === wf.id);
      if (idx >= 0) {
        const existing = cached[idx];
        if (
          existing.active !== wf.active ||
          existing.name !== wf.name ||
          JSON.stringify(existing.categories) !== JSON.stringify(wf.categories)
        ) {
          const newList = [...cached];
          newList[idx] = {
            ...existing,
            name: wf.name,
            active: wf.active,
            categories: wf.categories,
          };
          this.workflows$.next(newList);
        }
      }

      return {
        workflow: {
          id: wf.id,
          name: wf.name,
          active: wf.active,
          categories: wf.categories,
        },
        publishedVersion: data.publishedVersion,
        publishedDefinitionHash: data.publishedDefinitionHash,
      };
    } catch (error) {
      // The central API client throws sanitized HTTP/network errors. Only an
      // actual 404 means missing; outages and denied access remain load errors.
      if (error instanceof WorkspaceApiError && error.status === 404)
        return null;
      throw error;
    }
  }

  /** The steps, edges and schemas the workflow document holds. */
  getWorkflowDefinition(workflowId: string, signal?: AbortSignal) {
    return readWorkflowDocDefinition(
      this.workspaceService.workspace,
      workflowId,
      signal
    );
  }

  async preflightWorkflowPublish(
    workflowId: string
  ): Promise<WorkflowPublishPreflight> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/preflight`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return await response.json();
  }

  /**
   * sl-7xgk.2.1: Insert a step into a workflow.
   * Returns the updated viewer workflow payload so callers can reconcile
   * local graph state without issuing a separate getViewerState() round trip.
   */
  async insertStep(
    workflowId: string,
    body: {
      afterStepId?: string;
      outputIndex?: number;
      splitEdgeId?: string;
      stepType: string;
      stepName?: string;
      stepConfig?: Record<string, unknown>;
      branchType?: 'branch_xor' | 'branch_or' | 'branch_and';
    }
  ): Promise<{
    success: boolean;
    insertedStepId: string;
    insertedStepName: string;
    updatedWorkflow?: unknown;
    error?: string;
  }> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/insert-node`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );

    return await response.json();
  }

  /**
   * Parse SWR execution step results into nodeSteps format for logging.
   */
  parseExecutionToNodeSteps(execution: EngineExecutionDetail): Array<{
    name: string;
    nodeName: string;
    nodeType: string;
    status: 'success' | 'error';
    duration: number;
    itemCount: number;
    error?: string;
  }> {
    if (!execution.steps || execution.steps.length === 0) return [];

    return execution.steps.map(step => ({
      name: step.nodeName,
      nodeId: step.nodeId ?? undefined,
      nodeName: step.nodeName,
      nodeType: step.nodeType,
      status: step.status,
      duration: step.durationMs,
      itemCount: 0,
      error: step.error,
    }));
  }

  // ===========================================================================
  // sl-3yai.2.2: Marketplace API
  // ===========================================================================

  /**
   * sl-3yai.2.2: URL builder for marketplace endpoints
   * Routes: /api/workspace/:workspaceId/marketplace/*
   */
  private buildMarketplaceUrl(path: string): string {
    return `/api/workspace/${encodeURIComponent(this.workspaceId)}/marketplace${path}`;
  }

  /**
   * Search marketplace workflow templates with optional category filter and pagination.
   */
  async searchTemplates(
    query: string,
    category?: string,
    page?: number
  ): Promise<PaginatedResponse<WorkflowTemplate>> {
    const params = new URLSearchParams({ q: query });
    if (category) params.set('category', category);
    if (page !== undefined) params.set('page', String(page));

    const response = await this.request(
      this.buildMarketplaceUrl(`/templates?${params.toString()}`),
      { credentials: 'include' }
    );

    // workflow engine public API returns { workflows, totalWorkflows } — transform to PaginatedResponse
    const raw: {
      workflows: Array<{
        id: number;
        name: string;
        description?: string;
        totalViews: number;
        createdAt: string;
        categories: Array<{ id: number; name: string }>;
        nodes: Array<{ type: string; displayName: string }>;
        user: { username: string };
      }>;
      totalWorkflows: number;
    } = await response.json();

    const pageSize = 20;
    const currentPage = page ?? 1;

    return {
      items: raw.workflows.map(w => ({
        id: String(w.id),
        name: w.name,
        description: w.description,
        author: w.user?.username,
        downloads: w.totalViews,
        categories: (w.categories ?? []).map(c => c.name),
        nodeTypes: (w.nodes ?? []).map(n => n.type),
        nodeCount: (w.nodes ?? []).length,
        createdAt: w.createdAt,
        updatedAt: w.createdAt,
      })),
      total: raw.totalWorkflows,
      page: currentPage,
      pageSize,
    };
  }

  /**
   * Fetch all marketplace categories with template counts.
   */
  async fetchCategories(): Promise<Category[]> {
    const response = await this.request(
      this.buildMarketplaceUrl('/categories'),
      {
        credentials: 'include',
      }
    );

    // workflow engine public API returns { categories: [{id: number, name: string}] } — transform to Category[]
    const data: { categories: Array<{ id: number; name: string }> } =
      await response.json();
    return data.categories.map(c => ({
      id: String(c.id),
      name: c.name,
      count: 0,
    }));
  }

  /**
   * Analyze a marketplace template for compatibility before import.
   */
  async analyzeTemplate(templateId: string): Promise<CompatibilityEvaluation> {
    const response = await this.request(
      this.buildMarketplaceUrl(`/templates/${templateId}/analyze`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return await response.json();
  }

  /**
   * Analyze an existing workflow engine workflow for compatibility.
   */
  async analyzeExistingWorkflow(
    workflowId: string
  ): Promise<CompatibilityEvaluation> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/analyze`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return await response.json();
  }

  /**
   * Import a marketplace template into the workspace with optional auto-fix.
   */
  async importTemplate(
    templateId: string,
    options: ImportOptions
  ): Promise<ImportResult> {
    const response = await this.request(
      this.buildMarketplaceUrl(`/templates/${templateId}/import`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      }
    );

    return await response.json();
  }

  /**
   * Run schema discovery on a workflow to resolve dynamic node schemas.
   */
  async runDiscovery(workflowId: string): Promise<DiscoveryResult> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/discovery`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return await response.json();
  }

  // ===========================================================================
  // sl-3yai.2.2: Copilot AI API (sl-3yai.6.2 + 6.3)
  // ===========================================================================

  /**
   * Classify an unknown node using AI copilot.
   */
  async classifyNodeWithAI(
    node: WorkflowEngineNode,
    context: WorkflowContext
  ): Promise<ClassificationSuggestion> {
    const response = await this.request(
      this.buildMarketplaceUrl('/copilot/classify-node'),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node, context }),
      }
    );

    return await response.json();
  }

  /**
   * Accept a classification suggestion, creating a persistent rule.
   */
  async acceptClassificationSuggestion(
    suggestion: ClassificationSuggestion
  ): Promise<ClassificationRuleDTO> {
    const response = await this.request(
      this.buildMarketplaceUrl('/copilot/accept-classification'),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggestion }),
      }
    );

    return await response.json();
  }

  /**
   * Analyze migration feasibility for a node using AI copilot.
   */
  async analyzeMigrationWithAI(
    node: WorkflowEngineNode,
    context: WorkflowContext,
    findings: CompatibilityIssue[]
  ): Promise<CopilotMigrationResult> {
    const response = await this.request(
      this.buildMarketplaceUrl('/copilot/analyze-migration'),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node, context, findings }),
      }
    );

    return await response.json();
  }
}
